// FE-SCRM-051 — AddOpportunityModal.
//
// Three mount points (Renewal Board top-right, Prospect detail's
// Opportunities-list section in FE-SCRM-050, Opportunities list
// view in FE-SCRM-052). When ``preboundProspect`` is passed the
// prospect picker is hidden and the renewal anchor defaults from
// the prospect — that's the Prospect-detail call site. The other
// two call sites omit the prebound prop and the producer picks via
// the TargetSearchPicker.
//
// Stage is intentionally NOT a form field. The BE-SCRM-004 create
// endpoint defaults new opportunities to ``lead``; stage moves go
// through ``/move-stage`` so the audit history captures every
// transition. The "Starts in Lead" hint at the bottom of the modal
// makes that explicit for producers.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Modal } from "@/design-system";
import { ApiError } from "@/api/client";

import {
  CrmFormBanner,
  MoneyInput,
  TargetSearchPicker,
  type FormBannerError,
  type TargetSearchResult,
} from "../components";
import type { OpportunityType } from "../RenewalBoard/types";

import {
  fetchBrokerFirmsSuggestions,
  fetchBrokerOfficesSuggestions,
  searchProspectsForPicker,
  useBrokerLocationsQuery,
  useCreateOpportunityMutation,
  useTeamMembersQuery,
} from "./hooks";
import type {
  CreateOpportunityInput,
  PreboundProspect,
} from "./types";


interface Props {
  open: boolean;
  onClose: () => void;
  /** Prospect-detail call site (FE-SCRM-050) passes this. The two
   * list-style call sites (Renewal Board + Opportunities list)
   * pass ``null`` so the picker renders. */
  preboundProspect?: PreboundProspect | null;
}


// Shared input styling — mirrors the design-system ``<Input>`` token
// set (bg-raised over the modal's bg-raised body, distinguished by
// the stronger border-default rim + teal focus). Used everywhere a
// raw <input>/<select>/<textarea> appears in the modal so the form
// surface looks coherent against the design system.
const INPUT_CLS = [
  "rounded-md border border-border-default bg-raised",
  "px-2 py-1.5 text-sm text-text-primary",
  "placeholder:text-text-tertiary outline-none",
  "transition-fast ease-out",
  "focus-visible:border-teal focus-visible:shadow-focus",
  "disabled:cursor-not-allowed disabled:opacity-60",
].join(" ");


const OPPORTUNITY_TYPES: readonly OpportunityType[] = [
  "new_business", "bor_takeover", "rfp_response", "expansion",
] as const;


const OPPORTUNITY_TYPE_LABEL: Record<OpportunityType, string> = {
  new_business: "New business",
  bor_takeover: "BOR takeover",
  rfp_response: "RFP response",
  expansion: "Expansion at existing client",
};


const MONTHS: readonly { value: number; label: string }[] = [
  { value: 1, label: "January" }, { value: 2, label: "February" },
  { value: 3, label: "March" }, { value: 4, label: "April" },
  { value: 5, label: "May" }, { value: 6, label: "June" },
  { value: 7, label: "July" }, { value: 8, label: "August" },
  { value: 9, label: "September" }, { value: 10, label: "October" },
  { value: 11, label: "November" }, { value: 12, label: "December" },
];


function composeDefaultName(
  prospect_name: string | null,
  opportunity_type: OpportunityType,
): string {
  if (!prospect_name) return "";
  return `${prospect_name}: ${OPPORTUNITY_TYPE_LABEL[opportunity_type]}`;
}


function deriveBannerErrors(
  formError: string | null,
  serverError: unknown,
): FormBannerError[] {
  const out: FormBannerError[] = [];
  if (formError) out.push({ message: formError });
  if (serverError instanceof ApiError) {
    const detail = (
      typeof serverError.body.detail === "string"
        ? serverError.body.detail
        : null
    ) ?? serverError.message;
    out.push({ message: humanizeServerError(detail) });
  } else if (serverError instanceof Error) {
    out.push({ message: serverError.message });
  }
  return out;
}


function humanizeServerError(code: string): string {
  // The BE returns short slugs like ``invalid_renewal_month`` or
  // ``prospect_not_found``. Producers don't read snake_case so the
  // banner shows a friendlier sentence. Unknown codes pass through
  // verbatim so unhandled cases stay debuggable rather than silent.
  const map: Record<string, string> = {
    prospect_id_required: "Pick a prospect before continuing.",
    prospect_not_found: "That prospect is no longer available.",
    invalid_prospect_id: "Selected prospect is invalid — pick again.",
    invalid_opportunity_type: "Pick an opportunity type.",
    invalid_renewal_month: "Renewal month must be between 1 and 12.",
    invalid_renewal_year: "Renewal year must be a 4-digit year.",
    invalid_estimated_lives: "Estimated lives must be a positive number.",
    invalid_estimated_annual_revenue_cents:
      "Annual revenue must be a positive number.",
    invalid_estimated_commission_cents:
      "Commission must be a positive number.",
    invalid_probability: "Probability must be between 0 and 100.",
    invalid_expected_close_date: "Expected close date is invalid.",
    invalid_bor_target_date: "BOR target date is invalid.",
    invalid_bor_effective_date: "BOR effective date is invalid.",
  };
  return map[code] ?? code;
}


export function AddOpportunityModal({
  open, onClose, preboundProspect,
}: Props): JSX.Element | null {
  const navigate = useNavigate();
  const teamMembers = useTeamMembersQuery();
  const brokerLocations = useBrokerLocationsQuery();
  const create = useCreateOpportunityMutation();

  const [pickedProspect, setPickedProspect] =
    useState<TargetSearchResult | null>(null);
  const [opportunityType, setOpportunityType] =
    useState<OpportunityType>("new_business");
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [renewalMonth, setRenewalMonth] = useState<number>(
    preboundProspect?.renewal_month ?? (new Date().getMonth() + 1),
  );
  const [renewalYear, setRenewalYear] = useState<number>(
    preboundProspect?.renewal_year ?? new Date().getFullYear(),
  );
  const [estimatedLives, setEstimatedLives] = useState("");
  const [revenueCents, setRevenueCents] = useState<bigint | null>(null);
  const [commissionCents, setCommissionCents] = useState<bigint | null>(null);
  const [probability, setProbability] = useState("");
  const [expectedCloseDate, setExpectedCloseDate] = useState("");
  const [producerId, setProducerId] = useState<string>("");
  const [ownerId, setOwnerId] = useState<string>("");
  const [brokerLocationId, setBrokerLocationId] = useState<string>("");
  const [incumbentFirm, setIncumbentFirm] = useState("");
  const [firmSuggestions, setFirmSuggestions] = useState<string[]>([]);
  const [incumbentOffice, setIncumbentOffice] = useState("");
  const [officeSuggestions, setOfficeSuggestions] = useState<string[]>([]);
  const [nextStep, setNextStep] = useState("");
  const [borTargetDate, setBorTargetDate] = useState("");
  const [borEffectiveDate, setBorEffectiveDate] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const prospectName =
    preboundProspect?.prospect_name ?? pickedProspect?.label ?? null;
  const prospectId =
    preboundProspect?.prospect_id ?? pickedProspect?.id ?? null;

  // Reset on each open so a previous attempt doesn't bleed in. The
  // ``open`` flip is the only reset trigger — re-renders while open
  // (typing) must not clobber user input.
  useEffect(() => {
    if (!open) return;
    setPickedProspect(null);
    setOpportunityType("new_business");
    setName("");
    setNameTouched(false);
    setRenewalMonth(
      preboundProspect?.renewal_month ?? (new Date().getMonth() + 1),
    );
    setRenewalYear(
      preboundProspect?.renewal_year ?? new Date().getFullYear(),
    );
    setEstimatedLives("");
    setRevenueCents(null);
    setCommissionCents(null);
    setProbability("");
    setExpectedCloseDate("");
    setProducerId("");
    setOwnerId("");
    setBrokerLocationId("");
    setIncumbentFirm("");
    setIncumbentOffice("");
    setNextStep("");
    setBorTargetDate("");
    setBorEffectiveDate("");
    setFormError(null);
  }, [open, preboundProspect]);

  // Auto-compose the name from prospect + type until the producer
  // edits the field. The composition matches the spec's default
  // ``<prospect>: <opportunity_type>`` shape so producers don't
  // have to retype the common case.
  const composedName = useMemo(
    () => composeDefaultName(prospectName, opportunityType),
    [prospectName, opportunityType],
  );
  useEffect(() => {
    if (!nameTouched) setName(composedName);
  }, [composedName, nameTouched]);

  const bannerErrors = deriveBannerErrors(
    formError, create.isError ? create.error : null,
  );

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!prospectId) {
      setFormError("Pick a prospect before continuing.");
      return;
    }
    setFormError(null);

    const livesNum = estimatedLives.trim()
      ? Number(estimatedLives.trim()) : null;
    const probNum = probability.trim()
      ? Number(probability.trim()) : null;

    const input: CreateOpportunityInput = {
      prospect_id: prospectId,
      opportunity_type: opportunityType,
      renewal_month: renewalMonth,
      renewal_year: renewalYear,
    };
    if (name.trim() && name.trim() !== composedName) {
      input.name = name.trim();
    }
    if (livesNum !== null && Number.isFinite(livesNum)) {
      input.estimated_lives = livesNum;
    }
    if (revenueCents !== null) {
      input.estimated_annual_revenue_cents = Number(revenueCents);
    }
    if (commissionCents !== null) {
      input.estimated_commission_cents = Number(commissionCents);
    }
    if (probNum !== null && Number.isFinite(probNum)) {
      input.probability = probNum;
    }
    if (expectedCloseDate) input.expected_close_date = expectedCloseDate;
    if (producerId) input.producer_id = producerId;
    if (ownerId) input.owner_id = ownerId;
    if (brokerLocationId) input.broker_location_id = brokerLocationId;
    if (incumbentFirm.trim()) input.incumbent_broker_firm = incumbentFirm.trim();
    if (incumbentOffice.trim()) {
      input.incumbent_broker_office = incumbentOffice.trim();
    }
    if (nextStep.trim()) input.next_step = nextStep.trim();
    if (opportunityType === "bor_takeover") {
      if (borTargetDate) input.bor_target_date = borTargetDate;
      if (borEffectiveDate) input.bor_effective_date = borEffectiveDate;
    }

    create.mutate(input, {
      onSuccess: (opp) => {
        onClose();
        navigate(`/dashboard/sales-crm/opportunities/${opp.opportunity_id}`);
      },
    });
  }

  if (!open) return null;

  const producers = (teamMembers.data?.rows ?? []).filter(
    (m) => m.is_producer && m.status === "active",
  );
  const owners = (teamMembers.data?.rows ?? []).filter(
    (m) => m.status === "active",
  );
  const locations = (brokerLocations.data?.rows ?? []).filter(
    (l) => l.status === "active",
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add opportunity"
      size="lg"
      closeLabel="Close"
      footer={
        <>
          <button
            type="button"
            data-testid="add-opp-cancel"
            onClick={onClose}
            disabled={create.isPending}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-primary hover:bg-recessed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-opp-form"
            data-testid="add-opp-submit"
            disabled={create.isPending}
            className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90 disabled:opacity-60"
          >
            {create.isPending ? "Creating…" : "Create opportunity"}
          </button>
        </>
      }
    >
      <form
        id="add-opp-form"
        data-testid="add-opportunity-modal"
        onSubmit={submit}
        className="flex flex-col gap-3"
      >
        <CrmFormBanner errors={bannerErrors} />

        {/* Prospect ──────────────────────────────────────────── */}
        {preboundProspect ? (
          <Field label="Prospect">
            <div
              data-testid="add-opp-prebound-prospect"
              className="rounded-md border border-border-subtle bg-recessed px-3 py-2 text-sm text-text-primary"
            >
              <span className="font-medium">
                {preboundProspect.prospect_name}
              </span>
            </div>
          </Field>
        ) : (
          <Field label="Prospect" required>
            <div data-testid="add-opp-prospect-picker">
              <TargetSearchPicker
                value={pickedProspect}
                onChange={setPickedProspect}
                fetchResults={async (q) => searchProspectsForPicker(q)}
                restrictTo={["prospect"]}
                placeholder="Search prospects…"
              />
            </div>
          </Field>
        )}

        {/* Opportunity type 4-way + name ─────────────────────── */}
        <Field label="Opportunity type" required>
          <select
            id="field-opportunity_type"
            data-testid="add-opp-opportunity-type"
            value={opportunityType}
            onChange={(e) =>
              setOpportunityType(e.target.value as OpportunityType)
            }
            className={INPUT_CLS}
          >
            {OPPORTUNITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {OPPORTUNITY_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Opportunity name"
          hint="Auto-composed from the prospect and type — edit to customise."
        >
          <input
            id="field-name"
            data-testid="add-opp-name"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameTouched(true);
            }}
            placeholder={composedName}
            className={INPUT_CLS}
          />
        </Field>

        {/* Renewal anchor ────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Renewal month" required>
            <select
              id="field-renewal_month"
              data-testid="add-opp-renewal-month"
              value={renewalMonth}
              onChange={(e) => setRenewalMonth(Number(e.target.value))}
              className={INPUT_CLS}
            >
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Renewal year" required>
            <input
              id="field-renewal_year"
              data-testid="add-opp-renewal-year"
              type="number"
              min={2000}
              max={2099}
              value={renewalYear}
              onChange={(e) => setRenewalYear(Number(e.target.value))}
              className={INPUT_CLS}
            />
          </Field>
        </div>

        {/* Value + lives + probability ───────────────────────── */}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Estimated lives">
            <input
              data-testid="add-opp-estimated-lives"
              type="number"
              min={0}
              step={1}
              value={estimatedLives}
              onChange={(e) => setEstimatedLives(e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
          <Field label="Annual revenue">
            <MoneyInput
              id="field-estimated_annual_revenue_cents"
              value={revenueCents}
              onChange={setRevenueCents}
            />
          </Field>
          <Field label="Commission">
            <MoneyInput
              id="field-estimated_commission_cents"
              value={commissionCents}
              onChange={setCommissionCents}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Probability (%)">
            <input
              data-testid="add-opp-probability"
              type="number"
              min={0}
              max={100}
              step={1}
              value={probability}
              onChange={(e) => setProbability(e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
          <Field label="Expected close date">
            <input
              data-testid="add-opp-expected-close-date"
              type="date"
              value={expectedCloseDate}
              onChange={(e) => setExpectedCloseDate(e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
        </div>

        {/* Producer / owner / location ──────────────────────── */}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Producer">
            <select
              data-testid="add-opp-producer"
              value={producerId}
              onChange={(e) => setProducerId(e.target.value)}
              className={INPUT_CLS}
            >
              <option value="">— unassigned —</option>
              {producers.map((m) => (
                <option key={m.member_id} value={m.member_id}>
                  {m.first_name} {m.last_name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Owner">
            <select
              data-testid="add-opp-owner"
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              className={INPUT_CLS}
            >
              <option value="">— same as producer —</option>
              {owners.map((m) => (
                <option key={m.member_id} value={m.member_id}>
                  {m.first_name} {m.last_name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Broker location">
            <select
              data-testid="add-opp-broker-location"
              value={brokerLocationId}
              onChange={(e) => setBrokerLocationId(e.target.value)}
              className={INPUT_CLS}
            >
              <option value="">— unassigned —</option>
              {locations.map((l) => (
                <option key={l.location_id} value={l.location_id}>
                  {l.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {/* Incumbent broker autocomplete ─────────────────────── */}
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Incumbent broker firm"
            hint="Who they're currently with, if known."
          >
            <FirmAutocomplete
              value={incumbentFirm}
              onChange={(v) => {
                setIncumbentFirm(v);
                setIncumbentOffice("");
              }}
              suggestions={firmSuggestions}
              onQueryChange={async (q) => {
                try {
                  const res = await fetchBrokerFirmsSuggestions(q);
                  setFirmSuggestions(res.rows.map((r) => r.name));
                } catch {
                  setFirmSuggestions([]);
                }
              }}
              testId="add-opp-incumbent-firm"
            />
          </Field>
          <Field label="Incumbent broker office">
            <FirmAutocomplete
              value={incumbentOffice}
              onChange={setIncumbentOffice}
              suggestions={officeSuggestions}
              onQueryChange={async (q) => {
                try {
                  const res = await fetchBrokerOfficesSuggestions(
                    q, incumbentFirm.trim() || null,
                  );
                  setOfficeSuggestions(res.rows.map((r) => r.name));
                } catch {
                  setOfficeSuggestions([]);
                }
              }}
              testId="add-opp-incumbent-office"
            />
          </Field>
        </div>

        {/* BOR-only section ─────────────────────────────────── */}
        {opportunityType === "bor_takeover" && (
          <div className="grid grid-cols-2 gap-3 rounded-md border border-border-subtle bg-recessed/40 p-3">
            <Field label="BOR target date">
              <input
                data-testid="add-opp-bor-target-date"
                type="date"
                value={borTargetDate}
                onChange={(e) => setBorTargetDate(e.target.value)}
                className={INPUT_CLS}
              />
            </Field>
            <Field label="BOR effective date">
              <input
                data-testid="add-opp-bor-effective-date"
                type="date"
                value={borEffectiveDate}
                onChange={(e) => setBorEffectiveDate(e.target.value)}
                className={INPUT_CLS}
              />
            </Field>
          </div>
        )}

        {/* Next step ─────────────────────────────────────────── */}
        <Field label="Next step">
          <textarea
            data-testid="add-opp-next-step"
            value={nextStep}
            onChange={(e) => setNextStep(e.target.value)}
            rows={2}
            placeholder="e.g. Send discovery brief by Friday."
            className={INPUT_CLS}
          />
        </Field>

        <p className="text-xs text-text-tertiary">
          New opportunities start in the <strong>Lead</strong> stage —
          move them through the pipeline from the Renewal Board or
          the opportunity detail page.
        </p>
      </form>
    </Modal>
  );
}


function Field({
  label, hint, required, children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-text-primary">
        {label}
        {required ? <span className="ml-0.5 text-ember">*</span> : null}
      </span>
      {children}
      {hint ? (
        <span className="text-xs text-text-tertiary">{hint}</span>
      ) : null}
    </label>
  );
}


function FirmAutocomplete({
  value, onChange, suggestions, onQueryChange, testId,
}: {
  value: string;
  onChange: (v: string) => void;
  suggestions: readonly string[];
  onQueryChange: (q: string) => Promise<void>;
  testId: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <input
        data-testid={testId}
        type="text"
        value={value}
        onFocus={() => {
          setOpen(true);
          void onQueryChange(value);
        }}
        onBlur={() => {
          // Defer close so an onMouseDown on a suggestion fires
          // before we hide the list.
          setTimeout(() => setOpen(false), 150);
        }}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v);
          void onQueryChange(v);
        }}
        className={`w-full ${INPUT_CLS}`}
      />
      {open && suggestions.length > 0 && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-full z-20 mt-1 max-h-48 overflow-auto rounded-md border border-border-default bg-raised py-1 shadow-overlay"
        >
          {suggestions.map((s) => (
            <li
              key={s}
              role="option"
              aria-selected={s === value}
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(s);
                setOpen(false);
              }}
              className="cursor-pointer px-3 py-1 text-sm hover:bg-recessed"
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
